import { ID } from '@/types/model'
import { useState, useMemo } from 'react'
import { thermostatCostHistorySummaryCollection } from '@/reducers/thermostatCostHistorySummaryCollection'
import useMockableViewModel from '@/hooks/useMockableViewModel'
import mockData from '@/types/thermostatCostHistorySummary/mock.json'
import {
  ThermostatCostHistorySummary,
  ThermostatUsageInterval,
} from '@/types/thermostatCostHistorySummary'
import { useUsersPreferredTemperature } from '@/authenticated/hooks/useUsersPreferredTemperature'
import { ThermostatProvider } from '@/types/thermostat'
import { formatThermostatPeriodLabel } from './thermostatPeriod.utils'
import { centsToDollars } from '@/utils/currency'

export interface Props {
  thermostatId: ID
  thermostatName: ThermostatProvider
}

export interface ThermostatCostSavingsViewModel {
  thermostatId: ID
  thermostatName: ThermostatProvider
  loading: boolean
  periodLabel: string
  averageSetPoint: number
  totalCostCents: number
  costOfScheduleCents: number
  costOfAdjustmentsCents: number
  costOfOptiwattAdjustmentsCents: number
  totalKwhUsed: number
  totalOptiwattAdjustmentKwh: number
  usageIntervals: ThermostatUsageInterval[]
  avgHighTempF: number
  avgLowTempF: number
  maxTempF: number
  minTempF: number
  onChipGroupClick: (index: number | null) => void
  period: ThermostatCostSavingsDataPeriod
  setPeriod: (period: ThermostatCostSavingsDataPeriod) => void
  adjustmentCount: number
  periodDays: number
  savingsAmount: number
  monthSavingsAmount: number
  monthCostOfOptiwattAdjustmentsCents: number
}

export type ThermostatCostSavingsDataPeriod = 'day' | 'week' | 'month'

const costHistoryPeriods = ['day', 'week', 'month'] as const

function useViewModel(props: Props) {
  const { data: costHistoryList, isLoadingOrIdle: costHistoryLoading } =
    thermostatCostHistorySummaryCollection.useFetch({
      path: `thermostats/${props.thermostatId}/cost_history/summary`,
    })
  const costHistory = costHistoryList[0]
  const [period, setPeriod] = useState<ThermostatCostSavingsDataPeriod>('month')
  const costSummary = costHistory?.[period]
  const averageSetPoint = useUsersPreferredTemperature(
    costSummary?.average_set_point || 0,
  ).value
  const totalCostCents = costSummary?.total_cost_cents || 0
  const costOfScheduleCents = costSummary?.cost_of_schedule_cents || 0
  const costOfAdjustmentsCents = costSummary?.cost_of_adjustments_cents || 0
  const costOfOptiwattAdjustmentsCents =
    costSummary?.cost_of_optiwatt_adjustments_cents || 0
  const totalKwhUsed = costSummary?.total_kwh_used || 0
  const totalOptiwattAdjustmentKwh =
    costSummary?.total_optiwatt_adjustment_kwh || 0
  const usageIntervals =
    costSummary?.usage_intervals || ([] as ThermostatUsageInterval[])
  const avgHighTempF = useUsersPreferredTemperature(
    costSummary?.avg_high_temp_f || 0,
  ).value
  const avgLowTempF = useUsersPreferredTemperature(
    costSummary?.avg_low_temp_f || 0,
  ).value
  const maxTempF = useUsersPreferredTemperature(
    costSummary?.max_temp_f || 0,
  ).value
  const minTempF = useUsersPreferredTemperature(
    costSummary?.min_temp_f || 0,
  ).value

  function onChipGroupClick(index: number | null) {
    if (index === null) {
      return
    }
    setPeriod(costHistoryPeriods[index || 0])
  }

  const periodLabel = formatThermostatPeriodLabel(period)

  // Count adjustments from usageIntervals
  const adjustmentCount = useMemo(() => {
    return usageIntervals.filter(
      (interval) =>
        interval.usage.optiwatt_adjustments &&
        (interval.usage.optiwatt_adjustments.kwh_used > 0 ||
          interval.usage.optiwatt_adjustments.cost_cents !== 0),
    ).length
  }, [usageIntervals])

  // Calculate days for period
  const periodDays = period === 'day' ? 1 : period === 'week' ? 7 : 30 // month

  // Format savings amount (negative cost = positive savings)
  const savingsAmount = centsToDollars(Math.abs(costOfOptiwattAdjustmentsCents))

  // Get month period data for chip display
  const monthCostSummary = costHistory?.month
  const monthCostOfOptiwattAdjustmentsCents =
    monthCostSummary?.cost_of_optiwatt_adjustments_cents || 0
  const monthSavingsAmount = centsToDollars(
    Math.abs(monthCostOfOptiwattAdjustmentsCents),
  )

  return {
    ...props,
    loading: costHistoryLoading,
    periodLabel,
    averageSetPoint,
    totalCostCents,
    costOfScheduleCents,
    costOfAdjustmentsCents,
    totalOptiwattAdjustmentKwh,
    costOfOptiwattAdjustmentsCents,
    totalKwhUsed,
    usageIntervals,
    avgHighTempF,
    avgLowTempF,
    maxTempF,
    minTempF,
    onChipGroupClick,
    period,
    setPeriod,
    adjustmentCount,
    periodDays,
    savingsAmount,
    monthSavingsAmount,
    monthCostOfOptiwattAdjustmentsCents,
  } as ThermostatCostSavingsViewModel
}

function useMockViewModel(props: Props) {
  const costHistory = mockData as ThermostatCostHistorySummary
  const [period, setPeriod] = useState<ThermostatCostSavingsDataPeriod>('month')
  const costSummary = costHistory?.[period]
  const averageSetPoint = useUsersPreferredTemperature(
    costSummary?.average_set_point || 0,
  ).value
  const totalCostCents = costSummary?.total_cost_cents || 0
  const costOfScheduleCents = costSummary?.cost_of_schedule_cents || 0
  const costOfAdjustmentsCents = costSummary?.cost_of_adjustments_cents || 0
  const costOfOptiwattAdjustmentsCents =
    costSummary?.cost_of_optiwatt_adjustments_cents || 0
  const totalOptiwattAdjustmentKwh =
    costSummary?.total_optiwatt_adjustment_kwh || 0
  const totalKwhUsed = costSummary?.total_kwh_used || 0
  const usageIntervals =
    costSummary?.usage_intervals || ([] as ThermostatUsageInterval[])
  const avgHighTempF = useUsersPreferredTemperature(
    costSummary?.avg_high_temp_f || 0,
  ).value
  const avgLowTempF = useUsersPreferredTemperature(
    costSummary?.avg_low_temp_f || 0,
  ).value
  const maxTempF = useUsersPreferredTemperature(
    costSummary?.max_temp_f || 0,
  ).value
  const minTempF = useUsersPreferredTemperature(
    costSummary?.min_temp_f || 0,
  ).value

  function onChipGroupClick(index: number | null) {
    if (index === null) {
      return
    }
    setPeriod(costHistoryPeriods[index || 0])
  }

  // Count adjustments from usageIntervals
  const adjustmentCount = useMemo(() => {
    return usageIntervals.filter(
      (interval) =>
        interval.usage.optiwatt_adjustments &&
        (interval.usage.optiwatt_adjustments.kwh_used > 0 ||
          interval.usage.optiwatt_adjustments.cost_cents !== 0),
    ).length
  }, [usageIntervals])

  // Calculate days for period
  const periodDays = useMemo(() => {
    if (period === 'day') return 1
    if (period === 'week') return 7
    return 30 // month
  }, [period])

  // Format savings amount (negative cost = positive savings)
  const savingsAmount = useMemo(() => {
    return centsToDollars(Math.abs(costOfOptiwattAdjustmentsCents))
  }, [costOfOptiwattAdjustmentsCents])

  // Get month period data for chip display
  const monthCostSummary = costHistory?.month
  const monthCostOfOptiwattAdjustmentsCents =
    monthCostSummary?.cost_of_optiwatt_adjustments_cents || 0
  const monthSavingsAmount = useMemo(() => {
    return centsToDollars(Math.abs(monthCostOfOptiwattAdjustmentsCents))
  }, [monthCostOfOptiwattAdjustmentsCents])

  return {
    ...props,
    loading: false,
    periodLabel: formatThermostatPeriodLabel(period),
    averageSetPoint,
    totalCostCents,
    costOfScheduleCents,
    costOfAdjustmentsCents,
    costOfOptiwattAdjustmentsCents,
    totalOptiwattAdjustmentKwh,
    totalKwhUsed,
    usageIntervals,
    avgHighTempF,
    avgLowTempF,
    maxTempF,
    minTempF,
    onChipGroupClick,
    period,
    setPeriod,
    adjustmentCount,
    periodDays,
    savingsAmount,
    monthSavingsAmount,
    monthCostOfOptiwattAdjustmentsCents,
  }
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
